import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";
import { QrCode, Search, StopCircle } from "lucide-react";
import { api, euro, data, errMsg, STATO_CARD } from "@/lib/api";
import { formatCodice } from "@/pages/VerificaSaldo";

const Scanner = ({ onCode, onClose }) => {
  const ref = useRef(null);
  useEffect(() => {
    const qr = new Html5Qrcode("qr-reader");
    ref.current = qr;
    qr.start({ facingMode: "environment" }, { fps: 10, qrbox: 220 }, (txt) => { onCode(txt); }, () => {})
      .catch(() => { toast.error("Fotocamera non disponibile: inserisci il codice a mano"); onClose(); });
    return () => {
      if (qr.isScanning) qr.stop().catch(() => {});
    };
  }, [onCode, onClose]);
  return <div id="qr-reader" className="rounded-xl overflow-hidden border border-[#E3D9F0] max-w-sm" data-testid="qr-reader" />;
};

export default function Riscatto() {
  const [codice, setCodice] = useState("");
  const [card, setCard] = useState(null);
  const [importo, setImporto] = useState("");
  const [scan, setScan] = useState(false);
  const [busy, setBusy] = useState(false);

  const verifica = async (c = codice) => {
    try {
      const { data: d } = await api.post("/operatore/giftcard/verifica", { codice: c });
      setCard(d);
      setImporto(d.frazionabile ? "" : String(d.saldo_cent / 100).replace(".", ","));
    } catch (e) {
      setCard(null);
      toast.error(errMsg(e));
    }
  };

  const onCode = (txt) => {
    const c = formatCodice(txt);
    setScan(false);
    setCodice(c);
    verifica(c);
  };

  const riscatta = async () => {
    setBusy(true);
    try {
      const cent = Math.round(parseFloat(importo.replace(",", ".")) * 100);
      const { data: d } = await api.post("/operatore/riscatta", { codice, importo_cent: cent });
      setCard(d);
      setImporto("");
      toast.success(`Riscattati ${euro(cent)}. Saldo residuo ${euro(d.saldo_cent)}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="card space-y-5">
        <h3 className="font-display text-2xl">Riscatta una gift card</h3>
        <div className="flex gap-2">
          <input className="field font-code tracking-widest" placeholder="XXXX-XXXX-XXXX-XXXX" value={codice} onChange={(e) => setCodice(formatCodice(e.target.value))} data-testid="riscatto-codice-input" />
          <button className="btn-secondary" disabled={codice.length !== 19} onClick={() => verifica()} data-testid="riscatto-verifica-btn"><Search size={16} /></button>
        </div>
        <button className="btn-outline" onClick={() => setScan(!scan)} data-testid="riscatto-scan-btn">{scan ? <><StopCircle size={16} />Chiudi scanner</> : <><QrCode size={16} />Scansiona QR</>}</button>
        {scan && <Scanner onCode={onCode} onClose={() => setScan(false)} />}
      </div>
      {card && (
        <div className="card space-y-4 fade-up" data-testid="riscatto-card">
          <div className="flex justify-between">
            <p className="font-code">••••-••••-••••-{card.ultime4}</p>
            <span className={STATO_CARD[card.stato]} data-testid="riscatto-stato">{card.stato}</span>
          </div>
          <p className="font-display text-5xl text-[#7E5BAF]" data-testid="riscatto-saldo">{euro(card.saldo_cent)}</p>
          <p className="text-sm text-[#786E65]">Valore iniziale {euro(card.importo_cent)} · scade il {data(card.scadenza)} · {card.tipo_buono} · {card.frazionabile ? "frazionabile" : "non frazionabile"}</p>
          {card.stato === "attiva" && (
            <div className="flex gap-2 pt-2">
              <input className="field" inputMode="decimal" placeholder="Importo da scalare €" value={importo} disabled={!card.frazionabile}
                onChange={(e) => setImporto(e.target.value)} data-testid="riscatto-importo-input" />
              <button className="btn-primary" disabled={busy || !importo} onClick={riscatta} data-testid="riscatto-conferma-btn">Riscatta</button>
            </div>
          )}
          {card.tipo_buono === "monouso" && card.stato === "attiva" && <p className="text-xs text-amber-800">Buono monouso: dopo il riscatto l'eventuale residuo viene annullato.</p>}
        </div>
      )}
    </div>
  );
}
